import cn from 'classnames';
import { useState } from 'react';
import * as React from 'react';
import { animated } from 'react-spring';

import ScaleInOut from '../../animations/ScaleInOut';
import BulkEngDefaultIcon from '../../assets/images/icon-bulk-mock.svg';
import { ReactComponent as MultipleOptionsIcon } from '../../assets/images/icon-msku.svg';
import { useCurrency } from '../../contexts/CurrencyProvider';
import useWindowSize from '../../hooks/useWindowSize';
import { ICampaign } from '../../types/campaigns';
import { BUTTON_BUTTON } from '../../types/forms';
import { hasMskuItem } from '../../utils/inventories';
import { CampaignPreview, Price } from '../index';

import styles from './BoxItem.module.scss';

export interface IBoxItemProps {
  onClick: (box: ICampaign) => void;
  className?: string;
  iconClassName?: string;
  box: ICampaign;
  onEdit?: () => void;
  onDelete?: () => void;
  onSelect?: () => void;
  selected?: boolean;
  withPreview?: boolean;
  withWarning?: boolean;
  isDisabled?: boolean;
  dataIndex?: number;
}

const BoxItem: React.FC<IBoxItemProps> = ({
  onClick,
  className,
  iconClassName,
  box,
  onEdit,
  onDelete,
  onSelect,
  selected,
  withPreview,
  withWarning,
  isDisabled = false,
  dataIndex,
}) => {
  const { mobile } = useWindowSize();
  const { getCampaignTotal } = useCurrency();
  const [isHovered, setIsHovered] = useState(false);

  const total = React.useMemo(() => getCampaignTotal(box), [box, getCampaignTotal]);
  const hasMsku = React.useMemo(() => hasMskuItem(box.items), [box.items]);

  const campaignPreviewMarkup = React.useMemo(() => {
    return (
      <CampaignPreview showPreview={withPreview} showWarning={withWarning} campaign={box} className={styles.hint} />
    );
  }, [withPreview, withWarning, box]);

  const handleSelectClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onSelect?.();
    },
    [onSelect],
  );

  const handleBoxClick = React.useCallback(() => {
    if (!isDisabled) {
      onClick(box);
    }
  }, [box, onClick, isDisabled]);

  const dataAttribute = React.useMemo(() => {
    if (selected && isHovered) {
      return 'campaign_minus_btn';
    }
    if (selected) {
      return 'campaign_mark_btn';
    }
    return 'campaign_plus_btn';
  }, [selected, isHovered]);

  return (
    <div
      className={cn(styles.boxItem, { [styles.disabled]: isDisabled }, className)}
      data-action="campaign_btn"
      data-index={dataIndex}
    >
      {!mobile && campaignPreviewMarkup}
      <button type={BUTTON_BUTTON} onClick={handleBoxClick} disabled={isDisabled} data-action="campaign_card_button">
        <div className={cn(styles.iconInner, { [styles.disabled]: isDisabled }, iconClassName)}>
          {mobile && campaignPreviewMarkup}
          {!isDisabled && (
            <ScaleInOut trigger={!!onSelect}>
              {({ style }) => (
                <animated.div
                  style={style}
                  role={BUTTON_BUTTON}
                  className={cn(styles.selectBtn, { [styles.selected]: selected })}
                  onClick={handleSelectClick}
                  data-action={dataAttribute}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                />
              )}
            </ScaleInOut>
          )}
          <div
            className={styles.icon}
            style={{ backgroundImage: `url(${box.icon_url ? box.icon_url : BulkEngDefaultIcon})` }}
          />
          {hasMsku && <MultipleOptionsIcon className={styles.mskuIcon} />}
        </div>
        <div className={cn(styles.info, { [styles.disabled]: isDisabled })}>
          <span className={styles.title}>{box.name}</span>
          <div className={styles.priceWithControls}>
            <div className={styles.meta}>
              <span className={styles.description}>{box.description}</span>
              <Price value={total} className={styles.price} />
            </div>
            {(onEdit || onDelete) && (
              <div className={styles.controls}>
                {onEdit && (
                  <div
                    role={BUTTON_BUTTON}
                    className={styles.edit}
                    data-action="campaign_edit_button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                    }}
                  >
                    Edit
                  </div>
                )}
                {onDelete && (
                  <div
                    role={BUTTON_BUTTON}
                    className={styles.delete}
                    data-action="campaign_delete_button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                  >
                    Delete
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </button>
    </div>
  );
};

export default BoxItem;
